import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FingerprintIcon, EyeIcon, EyeOffIcon, LockIcon, ScanFaceIcon, CheckCircle2Icon, SmartphoneIcon, ArrowLeftIcon, CheckIcon, BuildingIcon } from 'lucide-react';
import { useTheme } from '../utils/theme';
interface LoginScreenProps {
  onLogin: () => void;
  onNavigateToSignup: () => void;
  onForgotPassword?: () => void;
}
let loginBiometricCounter = 0;
const ACCOUNTS = [{
  id: '1',
  holder: 'Shajia Khan',
  type: 'Current Account',
  accountNumber: '01008594839200',
  iban: 'BP05BARC12345601008594839200'
}, {
  id: '2',
  holder: 'Shajia Khan',
  type: 'Savings Account',
  accountNumber: '8726289202929',
  iban: 'BP05BARC98765432109876543210'
}, {
  id: '3',
  holder: 'Shajia Khan',
  type: 'Business Account',
  accountNumber: '5544332211009988',
  iban: 'BP05BARC55443322110099887766'
}, {
  id: '4',
  holder: 'Shajia Khan',
  type: 'Fixed Deposit Account',
  accountNumber: '1122334455667788',
  iban: 'BP05BARC11223344556677889900'
}];
export function LoginScreen({
  onLogin,
  onNavigateToSignup,
  onForgotPassword
}: LoginScreenProps) {
  const {
    isDark,
    isPlatinum
  } = useTheme();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [focusField, setFocusField] = useState<'username' | 'password' | null>(null);
  const [loginPhase, setLoginPhase] = useState<'idle' | 'scanning' | 'verified' | 'consent'>('idle');
  const [scanType, setScanType] = useState<'thumb' | 'face'>('thumb');
  // Digital consent state
  const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>([]);
  const [defaultAccountId, setDefaultAccountId] = useState<string | null>(null);
  const handleLogin = () => {
    const currentType = loginBiometricCounter % 2 === 0 ? 'thumb' : 'face';
    setScanType(currentType);
    loginBiometricCounter++;
    setLoginPhase('scanning');
    setTimeout(() => {
      setLoginPhase('verified');
      setTimeout(() => setLoginPhase('consent'), 1600);
    }, 2000);
  };
  const handleBiometric = (type: 'thumb' | 'face') => {
    setScanType(type);
    loginBiometricCounter++;
    setLoginPhase('scanning');
    setTimeout(() => {
      setLoginPhase('verified');
      setTimeout(() => setLoginPhase('consent'), 1600);
    }, 2000);
  };
  const toggleAccount = (id: string) => {
    const isSelected = selectedAccountIds.includes(id);
    if (isSelected) {
      setSelectedAccountIds(prev => prev.filter(x => x !== id));
      if (defaultAccountId === id) setDefaultAccountId(null);
    } else {
      setSelectedAccountIds(prev => [...prev, id]);
    }
  };
  const canSave = selectedAccountIds.length > 0 && defaultAccountId !== null;
  const pageBg = isPlatinum ? 'linear-gradient(180deg, #1C1C1E 0%, #2A2A2C 50%, #333336 100%)' : isDark ? 'linear-gradient(180deg, #1A0E08 0%, #2D1810 50%, #3D2518 100%)' : 'linear-gradient(180deg, #FFF8F2 0%, #FEEEE0 40%, #FBDDC8 80%, #F8CFB6 100%)';
  const inputBg = isPlatinum ? '#2C2C2E' : isDark ? '#2D1810' : '#FFFFFF';
  const inputTextColor = isPlatinum || isDark ? 'text-white' : 'text-[#303030]';
  const placeholderColor = isPlatinum ? 'placeholder:text-[#8E8E93]' : isDark ? 'placeholder:text-[#B8A89E]' : 'placeholder:text-[#B8B8BC]';
  const subtleText = isPlatinum ? 'text-[#A0A0A5]' : isDark ? 'text-[#C4B5AA]' : 'text-[#6E6F72]';
  const dividerSoft = isPlatinum ? '#48484A' : isDark ? '#3D2518' : '#E8C9B5';
  const biometricCircleBg = isPlatinum ? '#3A3A3C' : isDark ? '#3D2518' : '#FFFFFF';
  const inputIconColor = isPlatinum || isDark ? 'text-[#A0A0A5]' : 'text-[#9A9A9D]';
  // ─── DIGITAL CONSENT SCREEN ─────────────────────────────────────────────────
  if (loginPhase === 'consent') {
    return <motion.div initial={{
      opacity: 0,
      y: 24
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.45,
      ease: 'easeOut'
    }} className="w-full min-h-screen flex flex-col relative overflow-y-auto" style={{
      background: '#FFFCFA',
      fontFamily: "'Poppins', sans-serif",
      maxWidth: '390px',
      margin: '0 auto'
    }} data-id="element-1162">
        {/* Radial glow — exact from HTML */}
        <div className="absolute pointer-events-none" style={{
        top: '-80px',
        left: '-60px',
        width: '520px',
        height: '360px',
        background: 'radial-gradient(ellipse 55% 48% at 50% 36%, rgba(243,130,50,0.12) 0%, rgba(255,210,170,0.05) 50%, transparent 78%)',
        zIndex: 0
      }} data-id="element-1163" />

        <div style={{
        position: 'relative',
        zIndex: 1
      }} data-id="element-1164">
          {/* Header */}
          <div style={{
          padding: '24px 22px 0',
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }} data-id="element-1165">
            <button onClick={() => setLoginPhase('idle')} style={{
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center'
          }} data-id="element-1166">
              <ArrowLeftIcon style={{
              width: '18px',
              height: '18px',
              color: '#5A1408'
            }} strokeWidth={2} data-id="element-1167" />
            </button>
            <span style={{
            fontSize: '15px',
            color: '#5A1408',
            letterSpacing: '0.01em'
          }} className="font-[400]" data-id="element-1168">
              Digital Account Selection
            </span>
          </div>

          {/* Thin accent line */}
          <div style={{
          margin: '14px 22px 0',
          height: '1px',
          background: 'linear-gradient(to right, rgba(243,112,36,0.4), transparent)'
        }} data-id="element-1169" />

          {/* Body */}
          <div style={{
          padding: '22px 18px 32px',
          display: 'flex',
          flexDirection: 'column',
          gap: '14px'
        }} data-id="element-1170">
            {/* Subtitle */}
            <p style={{
            fontSize: '13px',
            color: '#6B6B6B',
            lineHeight: 1.65,
            margin: '0 2px'
          }} data-id="element-1171">
              Select the accounts you wish to use for mobile and internet
              banking.
            </p>

            {/* Account Cards */}
            {ACCOUNTS.map(account => {
            const isSelected = selectedAccountIds.includes(account.id);
            const isDefault = defaultAccountId === account.id;
            return <motion.div key={account.id} whileTap={{
              scale: 0.99
            }} onClick={() => toggleAccount(account.id)} style={{
              background: 'white',
              borderRadius: '18px',
              padding: '20px',
              border: isSelected ? '1.5px solid #F37024' : '1px solid #EBEBEB',
              boxShadow: isSelected ? '0 6px 24px rgba(243,112,36,0.09)' : '0 2px 10px rgba(0,0,0,0.04)',
              cursor: 'pointer',
              transition: 'border-color 0.15s, box-shadow 0.15s'
            }} data-id="element-1172">
                  {/* Top row: icon + name/type LEFT — DEFAULT pill + check circle RIGHT */}
                  <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '14px'
              }} data-id="element-1173">
                    {/* Left: bank icon + name */}
                    <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }} data-id="element-1174">
                      <div style={{
                    width: '38px',
                    height: '38px',
                    borderRadius: '12px',
                    background: isSelected ? '#FFF0E6' : '#F7F7F7',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }} data-id="element-1175">
                        <BuildingIcon style={{
                      width: '18px',
                      height: '18px',
                      color: isSelected ? '#F37024' : '#C0C0C0'
                    }} strokeWidth={1.8} data-id="element-1176" />
                      </div>
                      <div data-id="element-1177">
                        <p style={{
                      fontSize: '14px',
                      fontWeight: 600,
                      color: '#1A1A1A',
                      margin: 0
                    }} data-id="element-1178">
                          {account.holder}
                        </p>
                        <p style={{
                      fontSize: '11px',
                      color: '#9E9E9E',
                      margin: 0
                    }} data-id="element-1179">
                          {account.type}
                        </p>
                      </div>
                    </div>

                    {/* Right: DEFAULT pill (if default) + check circle */}
                    <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  gap: '6px'
                }} data-id="element-1180">
                      {isDefault && <span style={{
                    fontSize: '10px',
                    fontWeight: 600,
                    background: '#FFF0E6',
                    padding: '3px 10px',
                    borderRadius: '20px',
                    letterSpacing: '0.04em'
                  }} className="text-[#22C55E] bg-[#DCFFE8]" data-id="element-1181">
                          DEFAULT
                        </span>}
                      {/* Check circle — green when default, grey when not */}
                      <button type="button" onClick={e => {
                    e.stopPropagation();
                    if (!isSelected) {
                      setSelectedAccountIds(prev => [...prev, account.id]);
                    }
                    setDefaultAccountId(account.id);
                  }} style={{
                    width: '26px',
                    height: '26px',
                    borderRadius: '50%',
                    background: isDefault ? '#22C55E' : '#F0F0F0',
                    border: isDefault ? 'none' : '1.5px solid #E0E0E0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    padding: 0,
                    flexShrink: 0
                  }} aria-label="Set as default" data-id="element-1182">
                        <CheckIcon style={{
                      width: '14px',
                      height: '14px',
                      color: isDefault ? 'white' : '#C8C8C8'
                    }} strokeWidth={2.5} data-id="element-1183" />
                      </button>
                    </div>
                  </div>

                  {/* Divider */}
                  <div style={{
                height: '1px',
                background: isSelected ? '#FBF0E8' : '#F5F5F5',
                marginBottom: '14px'
              }} data-id="element-1184" />

                  {/* Details: ACCOUNT NO. + IBAN */}
                  <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '10px'
              }} data-id="element-1185">
                    <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }} data-id="element-1186">
                      <span style={{
                    fontSize: '11px',
                    fontWeight: 600,
                    color: isSelected ? '#F37024' : '#C0C0C0',
                    letterSpacing: '0.05em'
                  }} data-id="element-1187">
                        ACCOUNT NO.
                      </span>
                      <span style={{
                    fontSize: '12px',
                    color: '#1A1A1A',
                    fontWeight: 500
                  }} data-id="element-1188">
                        {account.accountNumber}
                      </span>
                    </div>
                    <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start'
                }} data-id="element-1189">
                      <span style={{
                    fontSize: '11px',
                    fontWeight: 600,
                    color: isSelected ? '#F37024' : '#C0C0C0',
                    letterSpacing: '0.05em',
                    paddingTop: '1px'
                  }} data-id="element-1190">
                        IBAN
                      </span>
                      <span style={{
                    fontSize: '11px',
                    color: '#5A5A5A',
                    textAlign: 'right',
                    maxWidth: '200px',
                    lineHeight: 1.5
                  }} data-id="element-1191">
                        {account.iban}
                      </span>
                    </div>
                  </div>
                </motion.div>;
          })}

            {/* Hint when accounts selected but no default yet */}
            {selectedAccountIds.length > 0 && !defaultAccountId && <p style={{
            fontSize: '11px',
            color: '#BBBBBB',
            textAlign: 'center',
            margin: 0,
            lineHeight: 1.6
          }} data-id="element-1192">
                Tap ✓ on a selected account to set it as your default.
              </p>}

            {/* Spacer */}
            <div style={{
            height: '12px'
          }} data-id="element-1193" />

            {/* SAVE button */}
            <motion.button whileTap={{
            scale: canSave ? 0.98 : 1
          }} disabled={!canSave} onClick={() => canSave && onLogin()} style={{
            background: canSave ? '#F37024' : '#F0C9A8',
            borderRadius: '8px',
            height: '55px',
            width: '280px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: canSave ? 'pointer' : 'default',
            boxShadow: canSave ? '0 8px 20px rgba(243,112,36,0.28)' : 'none',
            border: 'none',
            alignSelf: 'center'
          }} data-id="element-1194">
              <span style={{
              fontSize: '15px',
              fontWeight: 600,
              color: 'white',
              letterSpacing: '2px'
            }} data-id="element-1195">
                SAVE
              </span>
            </motion.button>

            {/* Footer note */}
            <p style={{
            fontSize: '11px',
            color: '#BBBBBB',
            textAlign: 'center',
            margin: 0,
            lineHeight: 1.6
          }} data-id="element-1196">
              You can change your default account at any time from settings.
            </p>
          </div>
        </div>
      </motion.div>;
  }
  // ─── MAIN LOGIN SCREEN ───────────────────────────────────────────────────────
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="w-full min-h-screen flex flex-col items-center px-5 pt-10 pb-6 relative overflow-hidden" style={{
    background: pageBg
  }} data-id="element-1197">
      {!isDark && !isPlatinum && <img src="https://cdn.magicpatterns.com/uploads/gpBvStKGKiCW9GDwDxtnLn/Login.png" alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover pointer-events-none select-none" data-id="element-1198" />}

      {!isDark && !isPlatinum && <div aria-hidden className="absolute inset-0 pointer-events-none overflow-hidden" data-id="element-1199">
          {[{
        top: '8%',
        left: '14%',
        size: 6,
        delay: 0
      }, {
        top: '12%',
        left: '78%',
        size: 5,
        delay: 0.6
      }, {
        top: '22%',
        left: '32%',
        size: 4,
        delay: 1.2
      }, {
        top: '34%',
        left: '88%',
        size: 5,
        delay: 0.3
      }, {
        top: '58%',
        left: '8%',
        size: 6,
        delay: 0.9
      }, {
        top: '68%',
        left: '72%',
        size: 5,
        delay: 1.5
      }, {
        top: '80%',
        left: '22%',
        size: 4,
        delay: 0.4
      }, {
        top: '88%',
        left: '60%',
        size: 5,
        delay: 1.1
      }].map((dot, i) => <motion.span key={i} className="absolute rounded-full" style={{
        top: dot.top,
        left: dot.left,
        width: dot.size,
        height: dot.size,
        background: '#F37024',
        boxShadow: '0 0 8px rgba(243,112,36,0.6)'
      }} animate={{
        opacity: [0.25, 0.85, 0.25],
        scale: [1, 1.25, 1]
      }} transition={{
        duration: 2.8,
        repeat: Infinity,
        delay: dot.delay,
        ease: 'easeInOut'
      }} data-id="element-1200" />)}
          <motion.div className="absolute rounded-full" style={{
        width: 550,
        height: 550,
        background: 'rgba(255,255,255,0.3)',
        filter: 'blur(15px)',
        translateX: '-50%',
        translateY: '-50%'
      }} animate={{
        left: ['0%', '100%'],
        top: ['50%', '0%']
      }} transition={{
        duration: 5,
        repeat: Infinity,
        repeatType: 'mirror',
        ease: 'easeInOut'
      }} data-id="element-1201" />
        </div>}

      {(isDark || isPlatinum) && <div className="absolute inset-0 pointer-events-none overflow-hidden" data-id="element-1202">
          <motion.div className="absolute rounded-full" style={{
        width: 380,
        height: 380,
        left: '-30%',
        top: '-8%',
        background: `radial-gradient(circle, ${isPlatinum ? '#8E8E93' : '#F8B58A'} 0%, transparent 70%)`,
        filter: 'blur(60px)',
        opacity: 0.18
      }} animate={{
        x: [0, 25, -8, 0],
        y: [0, 18, -4, 0],
        scale: [1, 1.08, 0.96, 1]
      }} transition={{
        duration: 16,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-1203" />
          <motion.div className="absolute rounded-full" style={{
        width: 300,
        height: 300,
        left: '-20%',
        top: '40%',
        background: `radial-gradient(circle, ${isPlatinum ? '#636366' : '#FBC8A8'} 0%, transparent 70%)`,
        filter: 'blur(55px)',
        opacity: 0.15
      }} animate={{
        x: [0, 18, -6, 0],
        y: [0, -12, 8, 0],
        scale: [1, 1.06, 0.96, 1]
      }} transition={{
        duration: 18,
        repeat: Infinity,
        ease: 'easeInOut',
        delay: 1
      }} data-id="element-1204" />
          <motion.div className="absolute rounded-full" style={{
        width: 260,
        height: 260,
        right: '-20%',
        top: '5%',
        background: `radial-gradient(circle, ${isPlatinum ? '#48484A' : '#FAD0B8'} 0%, transparent 70%)`,
        filter: 'blur(60px)',
        opacity: 0.1
      }} animate={{
        x: [0, -12, 6, 0],
        y: [0, 10, -6, 0]
      }} transition={{
        duration: 20,
        repeat: Infinity,
        ease: 'easeInOut',
        delay: 2
      }} data-id="element-1205" />
        </div>}

      <motion.div initial={{
      opacity: 0,
      y: -10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5,
      delay: 0.1
    }} className="w-full flex justify-center pt-6 pb-8 relative z-10" data-id="element-1206">
        <img src="https://cdn.magicpatterns.com/uploads/k5mzauqmFki77bNt7CFYh1/Whatsapp_digiBOP-removebg-preview.png" alt="digiBOP Logo" className="w-44 h-auto" data-id="element-1207" />
      </motion.div>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5,
      delay: 0.2
    }} className="w-full relative z-10" style={{
      width: '364.8px',
      height: '550px',
      borderRadius: '28px',
      padding: '50px 20px',
      background: 'rgba(255, 255, 255, 0.35)',
      backdropFilter: 'blur(40px)',
      WebkitBackdropFilter: 'blur(40px)',
      border: '1px solid #FFFFFF',
      boxShadow: 'inset 0px 1px 0px rgba(255, 255, 255, 0.60), 0px 8px 32px rgba(243, 112, 36, 0.08)'
    }} data-id="element-1208">
        <div className="flex items-center rounded-[8px] px-5 py-3.5 transition-all pt-[14px] pb-[14px] gap-[0px] mb-[24px]" style={{
        border: '1px solid transparent',
        backgroundImage: `linear-gradient(${inputBg}, ${inputBg}), linear-gradient(to right, #F37024, #FFFFFF)`,
        backgroundOrigin: 'border-box',
        backgroundClip: 'padding-box, border-box'
      }} data-id="element-1209">
          <SmartphoneIcon className={`w-[18px] h-[18px] mr-3 flex-shrink-0 ${inputIconColor}`} strokeWidth={1.8} data-id="element-1210" />
          <input type="text" value={username} onChange={e => setUsername(e.target.value)} onFocus={() => setFocusField('username')} onBlur={() => setFocusField(null)} placeholder="Username" className={`flex-1 outline-none text-[15px] bg-transparent ${inputTextColor} ${placeholderColor}`} data-id="element-1211" />
        </div>

        <div className="flex items-center rounded-[8px] px-5 py-3.5 transition-all mb-[16px]" style={{
        border: '1px solid transparent',
        backgroundImage: `linear-gradient(${inputBg}, ${inputBg}), linear-gradient(to right, #FFFFFF, #F37024)`,
        backgroundOrigin: 'border-box',
        backgroundClip: 'padding-box, border-box'
      }} data-id="element-1212">
          <LockIcon className={`w-[18px] h-[18px] mr-3 flex-shrink-0 ${inputIconColor}`} strokeWidth={1.8} data-id="element-1213" />
          <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} onFocus={() => setFocusField('password')} onBlur={() => setFocusField(null)} placeholder="Password" className={`flex-1 outline-none text-[15px] bg-transparent ${inputTextColor} ${placeholderColor}`} data-id="element-1214" />
          <button type="button" onClick={() => setShowPassword(!showPassword)} className="ml-2 flex-shrink-0" data-id="element-1215">
            {showPassword ? <EyeOffIcon className={`w-[18px] h-[18px] ${inputIconColor}`} strokeWidth={1.8} data-id="element-1216" /> : <EyeIcon className={`w-[18px] h-[18px] ${inputIconColor}`} strokeWidth={1.8} data-id="element-1217" />}
          </button>
        </div>

        <div className="w-full flex justify-end mb-[30px]" data-id="element-1218">
          <button onClick={onForgotPassword} className="text-[13px] font-medium text-[#F37024] hover:underline transition-all mt-[-15px]" data-id="element-1219">
            Forget Password?
          </button>
        </div>

        <div className="w-full flex justify-center" data-id="element-1220">
          <button onClick={handleLogin} disabled={loginPhase !== 'idle'} className="rounded-[8px] font-medium text-[16px] tracking-wide text-white transition-all active:scale-[0.99] disabled:opacity-60 mb-[50px]" style={{
          width: 240,
          height: 50,
          background: '#4A1106',
          boxShadow: '0 8px 20px rgba(74, 17, 6, 0.32)'
        }} data-id="element-1221">
            Login
          </button>
        </div>

        <div className="flex flex-col items-center pt-7 pb-1" data-id="element-1222">
          <span className={`text-[12px] font-medium mb-3.5 ${subtleText}`} style={{
          letterSpacing: '0.01em'
        }} data-id="element-1223">
            Biometric Login
          </span>
          <div className="flex items-start gap-12" data-id="element-1224">
            <button onClick={() => handleBiometric('thumb')} disabled={loginPhase !== 'idle'} className="flex flex-col items-center gap-1.5 group disabled:opacity-50" data-id="element-1225">
              <div className="w-[52px] h-[52px] rounded-full flex items-center justify-center transition-all group-hover:shadow-md group-active:scale-95" style={{
              background: biometricCircleBg,
              boxShadow: isPlatinum || isDark ? '0 4px 12px rgba(0,0,0,0.25)' : '0 4px 14px rgba(243,112,36,0.18)'
            }} data-id="element-1226">
                <FingerprintIcon className="w-6 h-6 text-[#F37024]" strokeWidth={1.6} data-id="element-1227" />
              </div>
              <span className={`text-[11.5px] font-medium ${subtleText}`} data-id="element-1228">
                Fingerprint
              </span>
            </button>
            <button onClick={() => handleBiometric('face')} disabled={loginPhase !== 'idle'} className="flex flex-col items-center gap-1.5 group disabled:opacity-50" data-id="element-1229">
              <div className="w-[52px] h-[52px] rounded-full flex items-center justify-center transition-all group-hover:shadow-md group-active:scale-95" style={{
              background: biometricCircleBg,
              boxShadow: isPlatinum || isDark ? '0 4px 12px rgba(0,0,0,0.25)' : '0 4px 14px rgba(243,112,36,0.18)'
            }} data-id="element-1230">
                <ScanFaceIcon className="w-6 h-6 text-[#F37024]" strokeWidth={1.6} data-id="element-1231" />
              </div>
              <span className={`text-[11.5px] font-medium ${subtleText}`} data-id="element-1232">
                Face ID
              </span>
            </button>
          </div>
        </div>
      </motion.div>

      <div className="flex-1 min-h-[24px]" data-id="element-1233" />

      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.5,
      delay: 0.4
    }} className="w-full max-w-[380px] flex flex-col items-center gap-5 pb-1 relative z-10 pt-[2px]" data-id="element-1234">
        <div className="flex flex-col items-center gap-2" data-id="element-1235">
          <p className={`text-[14px] ${subtleText}`} data-id="element-1236">
            Don't have an account?{' '}
            <button onClick={onNavigateToSignup} className="font-bold text-[#F37024] hover:underline" data-id="element-1237">
              Register now
            </button>
          </p>
          <div className="w-1 h-1 rounded-full" style={{
          background: '#F37024',
          opacity: 0.55
        }} data-id="element-1238" />
        </div>
        <div className={`flex items-center gap-2 text-[11px] tracking-wide ${subtleText}`} style={{
        opacity: 0.7
      }} data-id="element-1239">
          <span data-id="element-1240">Encrypted and Secured</span>
          <span style={{
          color: dividerSoft
        }} data-id="element-1241">
            |
          </span>
          <span data-id="element-1242">Version 2.0</span>
        </div>
      </motion.div>

      <AnimatePresence data-id="element-1243">
        {(loginPhase === 'scanning' || loginPhase === 'verified') && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 flex flex-col items-center justify-center" style={{
        background: pageBg,
        backdropFilter: 'blur(20px)'
      }} data-id="element-1244">
            <AnimatePresence mode="wait" data-id="element-1245">
              {loginPhase === 'scanning' && <motion.div key="scanning" initial={{
            opacity: 0,
            scale: 0.8
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.8
          }} className="flex flex-col items-center gap-6" data-id="element-1246">
                  <motion.div animate={{
              scale: [1, 1.2, 1],
              opacity: [0.6, 1, 0.6]
            }} transition={{
              duration: 1.2,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="w-24 h-24 rounded-full flex items-center justify-center" style={{
              background: 'rgba(243, 112, 36, 0.12)',
              border: '2px solid rgba(243, 112, 36, 0.3)'
            }} data-id="element-1247">
                    {scanType === 'thumb' ? <FingerprintIcon className="w-12 h-12 text-[#F37024]" strokeWidth={1.5} data-id="element-1248" /> : <ScanFaceIcon className="w-12 h-12 text-[#F37024]" strokeWidth={1.5} data-id="element-1249" />}
                  </motion.div>
                  <div className="text-center" data-id="element-1250">
                    <p className={`text-[18px] font-bold mb-1 ${isPlatinum || isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-1251">
                      {scanType === 'thumb' ? 'Scanning Fingerprint' : 'Scanning Face'}
                    </p>
                    <p className={`text-[13px] ${subtleText}`} data-id="element-1252">
                      {scanType === 'thumb' ? 'Place your finger on the sensor' : 'Look at the camera'}
                    </p>
                  </div>
                  <div className="w-48 h-1.5 bg-[#F37024]/10 rounded-full overflow-hidden" data-id="element-1253">
                    <motion.div initial={{
                width: '0%'
              }} animate={{
                width: '100%'
              }} transition={{
                duration: 2,
                ease: 'easeInOut'
              }} className="h-full bg-gradient-to-r from-[#F37024] to-[#E8943A] rounded-full" data-id="element-1254" />
                  </div>
                </motion.div>}
              {loginPhase === 'verified' && <motion.div key="verified" initial={{
            opacity: 0,
            scale: 0.8
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.8
          }} className="flex flex-col items-center gap-6" data-id="element-1255">
                  <motion.div initial={{
              scale: 0.3
            }} animate={{
              scale: 1
            }} transition={{
              type: 'spring',
              damping: 12,
              stiffness: 200
            }} className="w-24 h-24 rounded-full bg-[#22C55E]/15 flex items-center justify-center" style={{
              border: '2px solid rgba(34, 197, 94, 0.3)'
            }} data-id="element-1256">
                    <CheckCircle2Icon className="w-12 h-12 text-[#22C55E]" strokeWidth={1.5} data-id="element-1257" />
                  </motion.div>
                  <div className="text-center" data-id="element-1258">
                    <p className={`text-[18px] font-bold mb-1 ${isPlatinum || isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-1259">
                      Verified Successfully
                    </p>
                    <p className={`text-[13px] ${subtleText}`} data-id="element-1260">
                      Setting up your accounts...
                    </p>
                  </div>
                </motion.div>}
            </AnimatePresence>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}